import { Icon } from "@mdi/react";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { mdiContentSave } from "@mdi/js";
import { Tooltip } from "antd";
import { RootState } from "store/rootReducer";

import { StoredFeatureTemplate } from "../../api/featureTemplate";
import useUserModules from "../../hooks/useUserModules";
import { IconButton } from "../forecasting/Shared";
import SaveTemplateModal from "./SaveTemplateModal";
import { getDefaultTemplate } from "./defaultTemplate";
import { MlFeature, MlTarget, NormalizeFeaturePackage } from "./types";

/**
 * Checks whether two NormalizeFeaturePackage arrays are semantically equal.
 * Compares by ApplyTo.Property and NormalizeBy (Property, Value, Unit).
 * Used to determine if current normalization settings match an existing template.
 */
function areNormalizationsEqual(
  a: NormalizeFeaturePackage[],
  b: NormalizeFeaturePackage[]
): boolean {
  if (a.length !== b.length) return false;
  const mapA = new Map(a.map((item) => [item.ApplyTo.Property, item.NormalizeBy]));
  for (const item of b) {
    const matchA = mapA.get(item.ApplyTo.Property);
    if (!matchA) return false;
    if (
      matchA.Property !== item.NormalizeBy.Property ||
      matchA.Value !== item.NormalizeBy.Value ||
      matchA.Unit !== item.NormalizeBy.Unit
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Checks whether two MlTarget values are semantically equal.
 * Compares by Property (the unique identifier for the target).
 */
function areTargetsEqual(a?: MlTarget, b?: MlTarget): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.Property === b.Property;
}

interface SaveTemplateButtonProps {
  featureList: MlFeature[];
  templates: StoredFeatureTemplate[];
  normalizationFeatures: NormalizeFeaturePackage[];
  targetFeature?: MlTarget;
  onTemplateSaved: (savedTemplateName: string) => void;
}

const SaveTemplateButton: FC<SaveTemplateButtonProps> = ({
  featureList,
  templates,
  normalizationFeatures,
  targetFeature,
  onTemplateSaved
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const { has3dGeoModel } = useUserModules();

  const DEFAULT_TEMPLATE = useMemo(
    () => getDefaultTemplate(categoryList, has3dGeoModel),
    [categoryList, has3dGeoModel]
  );

  // Duplicate detection considers features, normalization, AND target.
  // This ensures that identical feature lists with different targets or
  // normalization settings are treated as distinct templates.
  const duplicateInfo = useMemo(() => {
    if (!featureList?.length) {
      return { isDuplicate: false, duplicateName: null };
    }

    // Check against DEFAULT_TEMPLATE
    if (
      DEFAULT_TEMPLATE.features?.length === featureList.length &&
      DEFAULT_TEMPLATE.features?.every((f, index) => {
        const currentFeature = featureList[index];
        return (
          f.Property === currentFeature.Property &&
          f.Monotonic === currentFeature.Monotonic
        );
      }) &&
      areNormalizationsEqual(
        DEFAULT_TEMPLATE.normalizationFeatures ?? [],
        normalizationFeatures
      ) &&
      areTargetsEqual(
        DEFAULT_TEMPLATE.targetFeature as MlTarget | undefined,
        targetFeature
      )
    ) {
      return { isDuplicate: true, duplicateName: DEFAULT_TEMPLATE.name };
    }

    // Check against saved templates
    const duplicateTemplate = templates.find((template) => {
      if (!template.features || template.features.length !== featureList.length)
        return false;
      const featuresMatch = template.features.every((f, index) => {
        const currentFeature = featureList[index];
        return (
          f.Property === currentFeature.Property &&
          f.Monotonic === currentFeature.Monotonic
        );
      });
      if (!featuresMatch) return false;
      if (
        !areNormalizationsEqual(
          template.normalizationFeatures ?? [],
          normalizationFeatures
        )
      )
        return false;
      return areTargetsEqual(
        template.targetFeature as MlTarget | undefined,
        targetFeature
      );
    });

    if (duplicateTemplate) {
      return { isDuplicate: true, duplicateName: duplicateTemplate.name };
    }

    return { isDuplicate: false, duplicateName: null };
  }, [featureList, templates, normalizationFeatures, targetFeature]);

  const tooltipTitle = duplicateInfo.isDuplicate
    ? `Same template named "${duplicateInfo.duplicateName}" already exists`
    : "Save template";

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <IconButton
          icon={<Icon path={mdiContentSave} size={"18px"} />}
          onClick={() => setModalOpen(true)}
          disabled={!featureList?.length || duplicateInfo.isDuplicate}
        />
      </Tooltip>
      <SaveTemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        featureList={featureList}
        templates={templates}
        onTemplateSaved={onTemplateSaved}
      />
    </>
  );
};

export default SaveTemplateButton;
